import React, { useEffect, useState } from "react";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { DataTable } from "../../components/table/DataTable";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import { getPaymentRequests, reset } from "../../redux/slice/liquidationSlice";
import { useNavigate } from "react-router-dom";
import { Landmark } from "lucide-react";
import { logout } from "../../redux/slice/accountSlice";
import { HeroHeader, Card, COLORS } from "../../designSystem";

function PaymentRequests() {
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const { paymentRequests, pageInfo, error, loading } = useSelector((state: RootState) => state.liquidation);
  const { token, isAuthenticated } = useSelector((state: RootState) => state.account);

  const [searchText, setSearchText] = useState("");
  const [groupBy, setGroupBy] = useState<string | undefined>();

  const columns = [
    {
      title: "#",
      key: "index",
      render: (_: any, __: any, index: number) =>
        (pageInfo.pageNumber - 1) * 10 + index + 1,
    },
    {
      title: "Requested User",
      dataIndex: "requestedUser",
      key: "requestedUser",
    },
    {
      title: "Request Date",
      dataIndex: "requestDate",
      key: "requestDate",
      render: (text: string) => new Date(text).toLocaleString(),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (text: string) => <span className="font-mono">{`₦${parseFloat(text).toLocaleString()}`}</span>,
    },
    {
      title: "Liquidation Status",
      dataIndex: "liqudationStatus",
      key: "liquidationStatus",
    },
    {
        title: "Actions",
        key: "actions",
        render: (_: any, record: any) => {
            const status = record.liqudationStatus?.toLowerCase();

            if (status === "processing") {
            return (
                <span className="text-yellow-600 font-medium italic">
                Needs Validation
                </span>
            );
            }

            if (status === "completed") {
            return (
                <button
                onClick={() => navigate(`/admin/approve-liquidation/${record.id}`)}
                className="bg-blue-100 text-blue-600 hover:bg-blue-200 font-medium px-4 py-1 rounded transition-all cursor-pointer border-none outline-none"
                >
                View
                </button>
            );
            }

            if (status === "payout pending") {
            return (
                <span className="text-purple-600 font-medium italic">
                Payout Pending
                </span>
            );
            }

            if (status === "rejected") {
            return (
                <span className="text-red-600 font-medium italic">
                Rejected
                </span>
            );
            }

            // Default: needs approval
            return (
          <div className="flex gap-2">
            <button
              onClick={() => navigate(`/admin/approve-liquidation/${record.id}`)}
              className="bg-green-600 hover:bg-green-700 text-white font-semibold px-4 py-1 rounded shadow-sm transition-all cursor-pointer border-none outline-none"
            >
              VFD Approve
            </button>

            <button
              onClick={() => navigate(`/admin/manual-approve-liquidation/${record.id}`)}
              className="bg-orange-600 hover:bg-orange-700 text-white font-semibold px-4 py-1 rounded shadow-sm transition-all cursor-pointer border-none outline-none"
            >
              Manual Approval
            </button>
          </div>
       );
        },
    }

  ];

  useEffect(() => {
    if (token) {
      dispatch(
        getPaymentRequests({
          token,
          searchParam: searchText,
          pageNumber: pageInfo?.pageNumber || 1,
        })
      ).catch((err) => console.log(err));
    }
  }, [token, searchText]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (error === "Unauthorized") {
      dispatch(reset());
      dispatch(logout());
      navigate("/login");
    }
  }, [error]);

  const handlePageChange = (pageNumber: number) => {
    dispatch(
      getPaymentRequests({
        token,
        searchParam: searchText,
        pageNumber,
      })
    ).catch((err) => console.log(err));
  };

  return (
    <AdminLayout>
      <main className="h-full overflow-y-auto px-6 py-8 md:px-8" style={{ background: COLORS.surface }}>
        <HeroHeader
          icon={Landmark}
          title="Payment Requests"
          subtitle="Review liquidation requests and approve payouts."
        />

        <Card className="mt-8 p-5">
          <DataTable
            columns={columns}
            data={paymentRequests}
            pageInfo={pageInfo}
            handlePageChange={handlePageChange}
            loading={loading}
            searchValue={searchText}
            onSearchChange={setSearchText}
            searchPlaceholder="Search by requester, reference or account"
            historyKey="payment-requests"
            grouping={{
              value: groupBy,
              onChange: setGroupBy,
              options: [
                {key: "liqudationStatus", label: "Status", get: (row) => row.liqudationStatus},
                {key: "requestedUser", label: "Requester", get: (row) => row.requestedUser},
                {
                  key: "day",
                  label: "Day requested",
                  get: (row) => row.requestDate ? new Date(row.requestDate).toLocaleDateString() : "",
                },
              ],
            }}
            exportFileName="payment-requests"
            emptyText="No payment requests match this search."
          />
        </Card>
      </main>
    </AdminLayout>
  );
}

export default PaymentRequests;
